'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/ui-new/Button';
import Input, { InputProps } from 'components/ui-new/Inputs/InputRHF';
import T from 'components/ui-new/Typography';
import { FormState } from 'features/auth/model/auth.form';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import useEnterPress from 'shared/hooks/useEnterPress';
import { z } from 'zod';

type Props<State extends FormState<object>> = {
  title?: string;
  children?: React.ReactNode;
  inputProps: React.InputHTMLAttributes<HTMLInputElement> & InputProps;
  action: (data: object) => Promise<State>;
  continueButtonCheckedFields?: string[];
  onActionSuccess?: (state: State) => void;
  onActionError?: (state: State) => void;
  schema: z.ZodSchema;
  defaultValues?: object;
};

const BaseTextForm = <State extends FormState<object>>({
  title,
  inputProps,
  children,
  action,
  onActionSuccess,
  schema,
  onActionError,
  defaultValues,
}: Props<State>) => {
  const t = useTranslations();
  const [showErrors, setShowErrors] = useState(false);
  const [isPending, startTransition] = useTransition();

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { handleSubmit, control, trigger, formState } = methods;

  useEffect(() => {
    if (formState.isDirty && !showErrors) {
      setShowErrors(true);
    }
  }, [formState.isDirty, showErrors]);

  const onSubmit = useCallback(() => {
    startTransition(async () => {
      try {
        const result = await action(methods.getValues());
        if (result.data != null) {
          onActionSuccess?.(result);
        } else if (result.message) {
          onActionError?.(result);
        }
      } catch (error) {
        onActionError?.({ message: 'Error occurred' } as State);
        console.error('Action error:', error);
      }
    });
  }, [action, onActionSuccess, onActionError, methods]);

  const manualTrigger = useCallback(() => {
    trigger().then((isValid) => {
      if (isValid) {
        handleSubmit(onSubmit)();
      } else {
        setShowErrors(true);
      }
    });
  }, [trigger, handleSubmit, onSubmit]);

  useEnterPress(manualTrigger, [methods, onSubmit, trigger]);

  return (
    <>
      <FormProvider {...methods}>
        {!!title && <T.Header.Header2 className='mb-[16px]'>{title}</T.Header.Header2>}
        <form
          className='flex flex-col gap-6 rounded-3xl sm:bg-Base-secondary-background sm:p-6'
          onSubmit={handleSubmit(onSubmit)}>
          <Input autoFocus={true} showError={showErrors} disabled={isPending} {...inputProps} />
          {children}
          <Button
            variant='premium'
            size='L sm:M'
            disabled={isPending || !formState.isValid}
            loading={isPending}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              manualTrigger();
            }}>
            {t('continue')}
          </Button>
        </form>
      </FormProvider>
    </>
  );
};

export default BaseTextForm;
